import React, { useState } from 'react';
import { NavLink as RouterLink, useLocation, useHistory } from 'umi';
import clsx from 'clsx';
import { Button, Collapse, ListItem, useTheme } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import SvgIconML from '@/components/SvgIconML/SvgIconML';
import webStorage from '@/utils/storageManager';
import { getToken } from '@/utils/token';

import { NavItemStyled } from './NavItemStyled';

// sessionStorage 中存储"开发中菜单最近一次被点击项"的 key，
// 配合 isDev 菜单的高亮判断使用。
const DEV_ACTIVE_STORAGE_KEY = 'SMG_DEV_ACTIVE_TITLE';

// sessionStorage 中存储"最近一次被点击的叶子菜单"对应的完整面包屑层级
// （包含父级到当前菜单的所有 title 数组 + 当前 href）。
// BreadcrumbsBar 直接读取这里渲染面包屑，不再依赖 URL_MENU / NEW_MENU_CONFIG。
const ACTIVE_CRUMB_STORAGE_KEY = 'SMG_ACTIVE_CRUMB';

function setActiveCrumb(href, titles) {
  try {
    sessionStorage.setItem(
      ACTIVE_CRUMB_STORAGE_KEY,
      JSON.stringify({ href, titles }),
    );
  } catch (e) {
    // sessionStorage 不可用时静默失败
  }
}

// 固定的目标路径：所有 isDev 菜单点击后统一跳转到的占位页。
const DEV_TARGET_PATH = '/develop';

// 拼装带 token / userId 的外链 URL。
// 走与 NavBar 票务系统按钮一致的拼接规则。
function buildExternalUrl(externalHref) {
  const token = getToken();
  const userId = webStorage.get('USER')?.id;
  const sep = externalHref.includes('?') ? '&' : '?';
  return `${process.env.SMG_URL}${externalHref}${sep}token=${token}&userId=${userId}`;
}

// 计算菜单项的三类关键值，统一收敛 href 相关的判断逻辑：
//   - isExternal：是否为外部链接（只有 externalHref 且无 href 时为 true）
//   - targetHref：点击后实际跳转的目标
//       isDev -> /develop（统一占位）
//       外链 -> 拼好的完整 URL
//       普通 -> href
//   - matchHref：用于判断"是否高亮"的路径基准
//       外链 -> 拼好的完整 URL
//       否则 -> href（不取 isDev 的 targetHref，避免多个 isDev 项同时高亮）
function resolveHrefs({ href, externalHref, isDev }) {
  const safeHref = href || '';
  const isExternal = Boolean(externalHref) && !safeHref;
  const externalUrl = isExternal ? buildExternalUrl(externalHref) : '';
  const targetHref = isDev
    ? DEV_TARGET_PATH
    : isExternal
    ? externalUrl
    : safeHref;
  const matchHref = isExternal ? externalUrl : safeHref;
  return { isExternal, targetHref, matchHref };
}

// 判断一个 matchHref 是否对应"当前路径"。
//   - matchHref 为空 -> false
//   - matchHref 是完整 URL（外链）-> 严格相等
//   - matchHref 是路径前缀 -> 当前路径必须以它开头，且后续字符是 '/' 或字符串末尾
//     （避免 '/platform' 误匹配 '/platformxxx'）
function isActivePath(pathname, matchHref) {
  if (!matchHref) {
    return false;
  }
  if (matchHref.startsWith('http://') || matchHref.startsWith('https://')) {
    return pathname === matchHref;
  }
  if (!pathname.startsWith(matchHref)) {
    return false;
  }
  const next = pathname.charAt(matchHref.length);
  return next === '' || next === '/';
}

// 判断当前菜单项是否应该以"外链 <a>"方式渲染（不走 umi RouterLink）：
//   - 是外部链接（拼了 token/userId 的完整 URL）
//   - 是开发中菜单（isDev），需要走 <a> 避免 umi NavLink 自动加 active
//   - 是历史遗留的特殊报告页路径
function shouldRenderAsAnchor({ isExternal, isDev, matchHref }) {
  return (
    isExternal ||
    isDev ||
    matchHref.includes('/report/cs') ||
    matchHref.includes('/report/store_list')
  );
}

function NavItem({
  title,
  href,
  externalHref,
  isDev,
  isChild,
  depth,
  children,
  icon,
  open: openProp,
  info: Info,
  isHide = false,
  auth,
  breadcrumbItems,
  ...rest
}) {
  // 当前路由信息：pathname 用于高亮匹配，history 用于编程式跳转。
  const location = useLocation();
  const { pathname } = location;
  const history = useHistory();

  const theme = useTheme();

  // 父菜单（带子项）的展开状态：默认跟随 openProp，由用户点击切换。
  const [open, setOpen] = useState(openProp);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  // 计算点击目标、高亮路径、外链标识等。
  const { isExternal, targetHref, matchHref } = resolveHrefs({
    href,
    externalHref,
    isDev,
  });

  // 当前菜单是否处于激活高亮态。
  //   - isDev 菜单：用 sessionStorage 记录"最近一次点击的菜单 title"，只有它会高亮
  //   - 其他菜单：按路径前缀匹配
  const isActive = isDev
    ? pathname.startsWith(DEV_TARGET_PATH) &&
      sessionStorage.getItem(DEV_ACTIVE_STORAGE_KEY) === title
    : isActivePath(pathname, matchHref);

  // 权限判断：无 auth 直接放行；否则要求用户 authorities 命中；
  // 特殊白名单 /bre 路径（历史遗留）也直接放行。
  const userAuths = webStorage.get('USER')?.authorities;
  const canAccess =
    !auth ||
    userAuths?.includes(auth) ||
    (matchHref && matchHref.includes('/bre'));

  // isDev 菜单的点击处理：阻止 <a> 的浏览器原生导航（避免页面刷新导致滚动位置丢失），
  // 记录激活项到 sessionStorage，再通过 React Router 客户端跳转。
  const handleDevClick = (e) => {
    e.preventDefault();
    sessionStorage.setItem(DEV_ACTIVE_STORAGE_KEY, title);
    if (matchHref && breadcrumbItems) {
      setActiveCrumb(matchHref, breadcrumbItems);
    }
    history.push(targetHref);
  };

  // 外部链接菜单的点击处理：阻止 <a> 的浏览器原生导航，
  // 改用 window.open 在新窗口打开，避免当前 SPA 页面被替换。
  const handleExternalClick = (e) => {
    e.preventDefault();
    if (matchHref && breadcrumbItems) {
      setActiveCrumb(matchHref, breadcrumbItems);
    }
    window.open(targetHref, '_blank', 'noopener,noreferrer');
  };

  // 叶子菜单项（普通内链）的通用点击处理：
  // 把当前菜单的完整层级菜单项对象数组存到 sessionStorage，
  // 供 BreadcrumbsBar 直接渲染（无需再走 NEW_MENU_CONFIG / URL_MENU）。
  // 不阻止默认导航，由 RouterLink 自行处理跳转。
  const handleLeafClick = () => {
    if (matchHref && breadcrumbItems) {
      setActiveCrumb(matchHref, breadcrumbItems);
    }
  };

  // 同函数供 ListItem onClick 复用（事件冒泡兜底，避免 RouterLink 吞掉 onClick）。
  const handleListItemClick = handleLeafClick;

  // 父菜单（带子项）：展示折叠面板，点击标题展开/收起。
  //   - isChild 节点：虽然 items 中有子项（用于面包屑链路识别），
  //     但在左侧菜单栏中作为叶子项显示，不展开子菜单。
  if (!isHide && children && canAccess && !isChild) {
    return (
      <NavItemStyled
        theme={theme}
        className={'item'}
        disableGutters
        key={title}
        {...rest}
      >
        <Button
          className={clsx('button', `depth-${depth}`)}
          onClick={handleToggle}
          style={null}
        >
          {open ? (
            <ExpandLessIcon size="small" color="inherit" />
          ) : (
            <ExpandMoreIcon size="small" color="inherit" />
          )}
          {icon && <SvgIconML name="1" />}
          <span className={'title'}>{title}</span>
        </Button>
        <Collapse in={open}>{children}</Collapse>
      </NavItemStyled>
    );
  }

  // 叶子菜单项：是否使用 <a> 渲染（外链 / isDev / 特殊报告路径）。
  const renderAsAnchor = shouldRenderAsAnchor({
    isExternal,
    isDev,
    matchHref,
  });

  // 叶子菜单项（无子项）：根据 renderAsAnchor 选择 <a> 或 <Button(RouterLink)>。
  return (
    <>
      {!isHide && canAccess && (
        <NavItemStyled
          theme={theme}
          className={'itemLeaf'}
          disableGutters
          key={title}
          {...rest}
        >
          <ListItem className="nav-item-li" onClick={handleListItemClick}>
            {renderAsAnchor ? (
              // 外链 / isDev / 历史报告页：使用原生 <a>。
              //   - isDev：onClick 阻止默认跳转，改走 React Router 客户端导航
              //   - 外链：onClick 阻止默认跳转，改用 window.open 在新窗口打开
              //   - 历史报告页：保留浏览器原生行为（href + target 由浏览器默认处理）
              <a
                href={targetHref}
                rel="noopener noreferrer"
                className={clsx(
                  'buttonLeaf',
                  isActive ? 'active' : '',
                  `depth-${depth}`,
                  isDev ? 'is-dev' : '',
                )}
                onClick={
                  isDev
                    ? handleDevClick
                    : isExternal
                    ? handleExternalClick
                    : handleLeafClick
                }
                style={{ display: 'inline-flex' }}
              >
                {icon && <SvgIconML name={icon} size={16} />}
                <span className={'title'}>{title}</span>
                {Info && <Info className={'info'} />}
              </a>
            ) : (
              // 普通内链菜单：走 umi 的 RouterLink，自动管理高亮与客户端路由。
              <Button
                className={clsx(
                  'buttonLeaf',
                  isActive ? 'active' : '',
                  `depth-${depth}`,
                  isDev ? 'is-dev' : '',
                )}
                component={RouterLink}
                exact
                style={null}
                to={targetHref}
                onClick={handleLeafClick}
              >
                {icon && <SvgIconML name={icon} size={16} />}
                <span className={'title'}>{title}</span>
                {Info && <Info className={'info'} />}
              </Button>
            )}
          </ListItem>
        </NavItemStyled>
      )}
    </>
  );
}

export default NavItem;
